/**
 * Sprint V102.i (Di 23.09.2026) — Trial-Countdown-Banner.
 *
 * Erscheint oben im Dashboard, wenn `license_tier === "trial"`. Zeigt
 * User transparent: Testzeitraum läuft, wird an XX.XX.YYYY automatisch
 * auf Pro Solo Monatlich (39 €/Monat) umgestellt.
 *
 * Warum eigene Komponente statt Erweiterung des LicenseDetailsModals:
 * V93.1 (15.09.2026) hat entschieden, dass das Modal keine Trial-Sprache
 * verwendet ("Pro Solo aktiv"). Der Banner ergänzt das mit Datum-Info
 * ohne die V93.1-Modal-Einheitlichkeit zu brechen.
 *
 * Wenn `trial_ends_at` nicht gesetzt ist oder in der Vergangenheit liegt,
 * rendert die Komponente `null` (keine leere Fläche).
 */
import React from "react";
import { Clock, X } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const diff = d.getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

export function TrialCountdownBanner({ user, onCancelTrial }) {
  const tier = (user?.license_tier || "").toLowerCase();
  const trialEndsAt = user?.trial_ends_at;

  if (tier !== "trial" || !trialEndsAt) return null;
  const days = daysUntil(trialEndsAt);
  if (days === null) return null;

  // Farbverlauf je nach Zeit-Nähe
  const urgent = days <= 2;
  const bgClass = urgent
    ? "bg-amber-50 dark:bg-amber-950/40 border-amber-300 dark:border-amber-800"
    : "bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-900";
  const iconClass = urgent
    ? "text-amber-600 dark:text-amber-400"
    : "text-emerald-600 dark:text-emerald-400";
  const textClass = urgent
    ? "text-amber-900 dark:text-amber-200"
    : "text-emerald-900 dark:text-emerald-200";

  const dayLabel = days === 0
    ? "heute"
    : days === 1
      ? "morgen"
      : `in ${days} Tagen`;

  return (
    <div
      data-testid="trial-countdown-banner"
      data-days-remaining={days}
      className={`rounded-xl border-2 ${bgClass} p-4 sm:p-5 mb-6`}
    >
      <div className="flex items-start gap-3">
        <div className={`flex-shrink-0 mt-0.5 ${iconClass}`}>
          <Clock className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm sm:text-base font-bold ${textClass}`}>
            Dein Testzeitraum endet {dayLabel} — am {formatDate(trialEndsAt)}.
          </p>
          <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1">
            Ab dem {formatDate(trialEndsAt)} läuft dein Zugang automatisch als <strong>Pro Solo · Monatlich (39 €/Monat)</strong> weiter — bequem hinterlegte Zahlungsdaten, kein weiterer Klick nötig.
          </p>
          {onCancelTrial && (
            <button
              type="button"
              onClick={onCancelTrial}
              data-testid="trial-cancel-button"
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-300 dark:border-slate-700 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
              Testzeitraum beenden
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default TrialCountdownBanner;
